import React, { useMemo, useState } from 'react';
import { formatPos, posChange, pctChange, mapScale } from './rankings';

export const UP = 'var(--color-success)';
export const DOWN = 'var(--color-danger)';
const SELECTED = '#2a78d6';
const REST = '#c4c7cc';

export const Dash = () => <span className="muted">—</span>;

export const Kpi = ({ label, value, sub, hint, spark }) => (
  <div className="kpi" title={hint}>
    <div className="kpi-label">{label}</div>
    <div className="kpi-value">{value}</div>
    {sub ? <div className="kpi-sub">{sub}</div> : null}
    {spark ? <div style={{ marginTop: 6 }}>{spark}</div> : null}
  </div>
);

/* The backend sends daily points up to 28 days and weekly beyond (search_console.trend_series). */
export const trendLabel = (metric, days, invert = false) =>
  `${metric} per ${days <= 28 ? 'day' : 'week'}, last ${days} days${invert ? ' (higher line = better)' : ''}`;

/* Number cell with a fixed-width sparkline after it, so the lines align down the column. */
export const TrendCell = ({ values, metric, days, invert, children }) => (
  <td className="num" style={{ whiteSpace: 'nowrap' }}>
    {children}{' '}
    <Sparkline values={values} label={trendLabel(metric, days, invert)} invert={invert} />
  </td>
);

/* Sortable header; position sorts ascending, everything else descending. */
export const SortTh = ({ k, sort, setSort, children, title, num = true }) => (
  <th className={`sortable${num ? ' num' : ''}`} title={title}
      aria-sort={sort === k ? (k === 'position' ? 'ascending' : 'descending') : undefined} onClick={() => setSort(k)}>
    {children}{sort === k ? (k === 'position' ? ' ▲' : ' ▼') : ''}
  </th>
);

/* "▲ 2.3" for a position that moved up (lower number), with the arrow carrying direction, not only colour. */
export const PosDelta = ({ cur, prev }) => {
  const d = posChange(cur, prev);
  if (d == null) return <span className="muted small">new</span>;
  if (Math.abs(d) < 0.1) return <span className="muted small">±0</span>;
  return <span className="small" style={{ color: d > 0 ? UP : DOWN }}>{d > 0 ? '▲' : '▼'} {Math.abs(d).toFixed(1)}</span>;
};

export const PctDelta = ({ cur, prev }) => {
  const d = pctChange(cur || 0, prev || 0);
  if (d == null) return <span className="muted small">new</span>;
  if (d === 0) return <span className="muted small">±0%</span>;
  return <span className="small" style={{ color: d > 0 ? UP : DOWN }}>{d > 0 ? '+' : ''}{d}%</span>;
};

/* Weekly average position, inverted so up = better.  One series: no legend; the
   column header names it.  Dashed line at 10.5 when the range crosses page one. */
export const PositionTrend = ({ weekly, width = 120, height = 30 }) => {
  const pts = (weekly || []).filter((w) => w.position != null);
  if (pts.length < 2) return <Dash />;
  const ps = pts.map((w) => w.position);
  const lo = Math.max(1, Math.min(...ps) - 0.5);
  const hi = Math.max(...ps) + 0.5;
  const step = (width - 6) / (pts.length - 1);
  const y = (p) => 3 + ((p - lo) / (hi - lo || 1)) * (height - 6);
  const line = pts.map((w, i) => `${3 + i * step},${y(w.position)}`).join(' ');
  const last = pts[pts.length - 1];
  const title = pts.map((w) => `${w.week}: ${formatPos(w.position)}`).join('\n');
  return (
    <svg width={width} height={height} style={{ display: 'block' }} role="img"
         aria-label={`Weekly position from ${formatPos(pts[0].position)} to ${formatPos(last.position)}`}>
      <title>{title}</title>
      {lo < 10.5 && hi > 10.5 && (
        <line x1="0" x2={width} y1={y(10.5)} y2={y(10.5)} stroke="var(--color-border)" strokeDasharray="3 3" />
      )}
      <polyline fill="none" stroke={SELECTED} strokeWidth="2" strokeLinejoin="round" points={line} />
      <circle cx={3 + (pts.length - 1) * step} cy={y(last.position)} r="3" fill={SELECTED} />
    </svg>
  );
};

/* Tiny trend line for a table cell: one series, no axes; the column names it.
   `invert` draws lower values higher (position: up = better).  Null points
   (no impressions that day) are skipped, not drawn as zero. */
export const Sparkline = ({ values, label, invert = false, width = 64, height = 18 }) => {
  const pts = (values || []).map((v, i) => [i, v]).filter(([, v]) => v != null);
  if (pts.length < 2) return <span style={{ display: 'inline-block', width }} />;
  const vs = pts.map(([, v]) => v);
  const lo = Math.min(...vs);
  const hi = Math.max(...vs);
  const step = (width - 4) / Math.max(1, values.length - 1);
  const y = (v) => {
    const t = (v - lo) / (hi - lo || 1);
    return 2 + (invert ? t : 1 - t) * (height - 4);
  };
  const line = pts.map(([i, v]) => `${2 + i * step},${y(v)}`).join(' ');
  const [li, lv] = pts[pts.length - 1];
  return (
    <svg width={width} height={height} style={{ display: 'inline-block', verticalAlign: 'middle', flex: 'none' }} role="img" aria-label={label}>
      <title>{label}</title>
      <polyline fill="none" stroke={SELECTED} strokeWidth="1.5" strokeLinejoin="round" points={line} />
      <circle cx={2 + li * step} cy={y(lv)} r="2" fill={SELECTED} />
    </svg>
  );
};

/* Topic map: pages (squares) and queries (dots, area ~ impressions) placed by
   their embeddings (2D PCA).  Clusters can number up to ten, more than a
   categorical palette separates on a scatter, so every point is neutral and
   the selected cluster is drawn in one accent.  Hover a point for its name. */
export const TopicMap = ({ map, selected, onSelect, width = 640, height = 380 }) => {
  const [hover, setHover] = useState(null);
  const all = useMemo(() => [...(map?.pages || []), ...(map?.queries || [])], [map]);
  const scale = useMemo(() => mapScale(all, width, height, 16), [all, width, height]);
  if (!all.length) return null;
  const maxImpr = Math.max(1, ...(map.queries || []).map((q) => q.impressions || 0));
  const r = (q) => 2.5 + 7 * Math.sqrt((q.impressions || 0) / maxImpr);
  const isSel = (c) => selected != null && c === selected;
  // draw unselected first so the selected cluster sits on top
  const order = (items) => [...items].sort((a, b) => Number(isSel(a.cluster)) - Number(isSel(b.cluster)));
  return (
    <div style={{ position: 'relative', maxWidth: width }}>
      <svg viewBox={`0 0 ${width} ${height}`} width="100%" role="img"
           aria-label="Topic map of pages and search queries" style={{ display: 'block', background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 8 }}>
        {order(map.queries || []).map((q, i) => {
          const [x, yy] = scale(q);
          return (
            <circle key={`q${i}`} cx={x} cy={yy} r={r(q)} fill={isSel(q.cluster) ? SELECTED : REST}
                    fillOpacity={isSel(q.cluster) ? 0.75 : 0.45} stroke="var(--color-surface)" strokeWidth="1"
                    onMouseEnter={() => setHover({ x, y: yy, text: `${q.query} · ${q.impressions} impr · pos ${formatPos(q.position)}` })}
                    onMouseLeave={() => setHover(null)} onClick={() => onSelect && onSelect(q.cluster)} style={{ cursor: 'pointer' }} />
          );
        })}
        {order(map.pages || []).map((p, i) => {
          const [x, yy] = scale(p);
          return (
            <rect key={`p${i}`} x={x - 5} y={yy - 5} width="10" height="10" rx="2"
                  fill={isSel(p.cluster) ? SELECTED : 'var(--color-surface)'} stroke={isSel(p.cluster) ? SELECTED : '#6b7280'} strokeWidth="2"
                  onMouseEnter={() => setHover({ x, y: yy, text: p.page })} onMouseLeave={() => setHover(null)}
                  onClick={() => onSelect && onSelect(p.cluster)} style={{ cursor: 'pointer' }} />
          );
        })}
      </svg>
      {hover && (
        <div className="small" style={{
          position: 'absolute', left: `${(hover.x / width) * 100}%`, top: `${(hover.y / height) * 100}%`,
          transform: 'translate(-50%, calc(-100% - 10px))', background: 'var(--color-text)', color: '#fff',
          padding: '4px 8px', borderRadius: 4, pointerEvents: 'none', whiteSpace: 'nowrap', maxWidth: 420,
          overflow: 'hidden', textOverflow: 'ellipsis',
        }}>{hover.text}</div>
      )}
      <div className="muted small" style={{ marginTop: 6 }}>
        <span style={{ display: 'inline-block', width: 9, height: 9, border: '2px solid #6b7280', borderRadius: 2, verticalAlign: 'middle' }} /> page
        {'  '}<span style={{ display: 'inline-block', width: 10, height: 10, background: REST, borderRadius: '50%', verticalAlign: 'middle', marginLeft: 10 }} /> query (size = impressions)
        {'  '}<span style={{ display: 'inline-block', width: 10, height: 10, background: SELECTED, borderRadius: '50%', verticalAlign: 'middle', marginLeft: 10 }} /> selected topic
        {' · '}Closer together = closer in meaning. Click a point or a topic row to highlight it.
      </div>
    </div>
  );
};
